import React from 'react';
import { X, FileText, Clock, CheckCircle, ArrowRight } from 'lucide-react';

interface PhaseSummary {
  phase: string;
  title: string;
  content: string;
  timestamp: string;
  questionsAnswered: number;
}

interface SummaryReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  summaries: PhaseSummary[];
}

const SummaryReviewModal: React.FC<SummaryReviewModalProps> = ({
  isOpen,
  onClose,
  summaries
}) => {
  if (!isOpen) return null;

  const getPhaseDisplayName = (phase: string) => {
    switch (phase) {
      case 'phase1': return 'Phase 1';
      case 'phase2': return 'Phase 2'; 
      case 'project': return 'Project Phase';
      case 'final': return 'Final Phase';
      default: return phase;
    }
  };

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'Phase1: Summary Checkpoint': return 'from-blue-500 to-blue-600';
      case 'Phase2: Deepdive Questions': return 'from-green-500 to-green-600';
      case 'project': return 'from-purple-500 to-purple-600';
      case 'final': return 'from-pink-500 to-pink-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const formatMarkdownContent = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  console.log("Summaries:", summaries);
  return (
    <div style={{zIndex:"999999"}} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pl-6 pt-3 pr-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-1">
              Complete Interview Summary
            </h2>
            <p className="text-gray-600">
              Review all phase summaries from your knowledge transfer interview
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-80 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {summaries.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Summaries Available</h3>
              <p className="text-gray-500">Complete interview phases to see summaries here.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Summary Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600 mb-2">{summaries.length}</div>
                  <div className="text-sm text-blue-800 font-medium">Phases Completed</div>
                </div>
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                  <div className="text-2xl font-bold text-green-600 mb-2">
                    {summaries.reduce((total, summary) => total + summary.questionsAnswered, 0)}
                  </div>
                  <div className="text-sm text-green-800 font-medium">Total Questions</div>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600 mb-2">
                    {summaries.length > 0 ? formatTimestamp(summaries[0].timestamp).split(',')[0] : 'N/A'}
                  </div>
                  <div className="text-sm text-purple-800 font-medium">Interview Date</div>
                </div>
                <div className="bg-gradient-to-r from-pink-50 to-pink-100 rounded-xl p-6 border border-pink-200">
                  <div className="text-2xl font-bold text-pink-600 mb-2">100%</div>
                  <div className="text-sm text-pink-800 font-medium">Progress Complete</div>
                </div>
              </div>

              {/* Phase Timeline */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Interview Timeline</h3>
                <div className="relative">
                  {summaries.map((summary, index) => (
                    <div key={summary.phase} className="flex items-center mb-6 last:mb-0">
                      <div className={`w-12 h-12 bg-gradient-to-r ${getPhaseColor(summary.phase)} rounded-full flex items-center justify-center shadow-lg z-10`}>
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="ml-6 flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {getPhaseDisplayName(summary.phase)}
                          </h4>
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTimestamp(summary.timestamp)}
                          </span>
                        </div>
                        <p className="text-gray-600">{summary.questionsAnswered} questions completed</p>
                      </div>
                      {index < summaries.length - 1 && (
                        <div className="absolute left-6 top-12 w-0.5 h-6 bg-gray-300"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Detailed Summaries */}
              <div className="space-y-8">
                <h3 className="text-xl font-semibold text-gray-900">Detailed Phase Summaries</h3>
                {summaries.map((summary, index) => (
                  <div key={summary.phase} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-10 h-10 bg-gradient-to-r ${getPhaseColor(summary.phase)} rounded-lg flex items-center justify-center shadow-md`}>
                        <span className="text-white font-bold text-sm">{index + 1}</span>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-900">{summary.title}</h4>
                        <p className="text-gray-600">{getPhaseDisplayName(summary.phase)} • {formatTimestamp(summary.timestamp)}</p>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <div 
                        className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ 
                          __html: formatMarkdownContent(summary.content) 
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => window.print()}
            style={{ background: '#bb1ccc' }}
            className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-lg font-medium hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-md"
          >
            <FileText className="w-4 h-4" />
            Print Summary
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummaryReviewModal;